import { Close } from "@mui/icons-material"
import { Stack, Typography, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material"
import * as React from "react"
import { getAllPools, Pool } from "../../api/pools-api"
import { $allPools } from "../../stores/pools-store"
import { SearchContext, SearchProvider } from "@/context/SearchContext"
import { SearchResultsExactMatch, useSearchResults } from "@/hooks/useSearchResults"
import { SearchInput } from "../SearchBar/SearchInput"
import { SearchResults } from "../SearchBar/SearchResults"
import { useDebounce } from "@/hooks/useDebounce"
import { useQuery } from "@tanstack/react-query"
import { ErrorBoundary } from "../ErrorBoundaryNew"

function SearchBarInside() {
  const { dialogOpen, setDialogOpen, currentValue, setCurrentValue } =
    React.useContext(SearchContext)
  const debouncedValue = useDebounce(currentValue, 500) // 300ms delay
  const { options, groupedPools, exactMatch, loading } = useSearchResults(debouncedValue)

  const { data, error, isLoading } = useQuery({
    queryKey: ["allPools"],
    queryFn: getAllPools,
    staleTime: 1000 * 60 * 30, // 30 minutes
    throwOnError: true,
  })

  React.useEffect(() => {
    if (!data) return

    console.log("onAllPools", data)
    $allPools.set(data)
  }, [data])

  function handleClose() {
    setDialogOpen(false)
    setCurrentValue("")
    setDialogOpen(false)
  }

  // New: Listen for "Cmd + K" key press and open dialog if not already focused on an input element
  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Check if no input is focused and key pressed is "/"
      const activeTag = document.activeElement?.tagName.toLowerCase()
      if (
        activeTag === "input" ||
        activeTag === "textarea" ||
        document.activeElement?.hasAttribute("contenteditable")
      ) {
        return
      }
      const isMac = navigator.userAgent.includes("Mac")
      if (
        (isMac && e.metaKey && e.key.toLowerCase() === "k") ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === "k")
      ) {
        e.preventDefault()
        setDialogOpen(true)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [setDialogOpen])

  return (
    <>
      {/* Input-like container to open the dialog */}
      <div
        onClick={() => setDialogOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setDialogOpen(true)
          }
        }}
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer" }}
      >
        <SearchInput />
      </div>
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ padding: "0" }}>
          <Stack gap={0.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" sx={{ color: "rgba(235, 185, 206, 1)", px: 1 }}>
                Search
              </Typography>
              <IconButton onClick={handleClose} edge="end">
                <Close fontSize="medium" />
              </IconButton>
            </Stack>
            <SearchInput />
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          {currentValue.trim() === "" ? (
            <Stack alignItems="center" pt={4}>
              <Typography variant="body1" color="textSecondary">
                Start typing to search for pools, tokens, or projects...
              </Typography>
            </Stack>
          ) : (
            <ErrorBoundary>
              <SearchResults
                options={options as any}
                groupedPools={groupedPools as any}
                exactMatch={exactMatch as SearchResultsExactMatch}
                handleClose={handleClose}
                loading={loading}
              />
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export function SearchBar() {
  return (
    <SearchProvider>
      <SearchBarInside />
    </SearchProvider>
  )
}
